import { useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { PaymentFormTotals } from 'src/app/components/checkout/payment-form';
import { useCart } from 'src/queries/curbside/get-cart';
import { useAppSelector } from 'src/utils';
import { useHasPreauth } from 'src/utils/hooks/use-has-preauth';
import { useIsDutchiePay } from 'src/utils/hooks/use-is-dutchie-pay';
import React from 'react';

export enum FeePaymentMethod {
  DutchiePay = 1,
  PinDebit = 2,
  Credit = 3,
}

type UsePaymentReturn = {
  handlePaymentFailure: () => void;
  handlePaymentSuccess: () => void;
  showCheckoutSuccess: boolean;
  totals: PaymentFormTotals;
};

export const usePayment = (): UsePaymentReturn => {
  // Global state
  const changeAmountDue = useAppSelector((state) => state.mobileCheckout.changeAmountDue) ?? 0;
  const totalAmountPaid = useAppSelector((state) => state.mobileCheckout.totalAmountPaid) ?? 0;
  const isCheckoutComplete = useAppSelector((state) => state.mobileCheckout.isCheckoutComplete);
  const activeCart = useAppSelector((state) => state.mobileCheckout.activeCart);

  // Local state
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(isCheckoutComplete);
  const ldClient = useLDClient();
  const generiPaySpinOnMobileCheckoutEnabled = ldClient?.variation('fintech.retail-payments.generipay-spin-on-mobile-checkout.rollout', true);

  // Hooks
  const hasPreauth = useHasPreauth();
  const isDutchiePay = useIsDutchiePay();
  const { data: cart } = useCart();

  // Use cart data from Redux store if available, otherwise fall back to API data
  const cartData = activeCart || cart;

  // Computed
  const originalTotals: PaymentFormTotals = {
    total: cart?.GrandTotalRounded ?? 0,
    subtotal: cart?.SubTotal ?? 0,
    tax: cart?.Tax ?? 0,
    discount: cart?.TotalDiscountAndLoyalty ?? 0,
    rounding: cart?.RoundingAmount ?? 0,
    feesDonations: cart?.FeesAndDonations ?? 0,
    changeDue: hasPreauth ? 0 : changeAmountDue,
    totalPaid: hasPreauth ? (cart?.GrandTotalRounded ?? 0) : totalAmountPaid,
    tip: cart?.TipAmount ?? 0,
    dutchiePayFees: isDutchiePay
      ? ((cart?.FeesDonations ?? [])
          .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.DutchiePay)
          .map((fd) => fd.CashValue)
          .reduce((sum, cv) => sum + cv, 0) ?? 0)
      : 0,
    nonCashAdjustmentFees:
      (cart?.FeesByPaymentType ?? [])
        .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.PinDebit)
        .map((fd) => fd.CashValue)
        .reduce((sum, cv) => sum + cv, 0) ?? 0,
    creditNonCashAdjustmentFees:
      (cart?.FeesByPaymentType ?? [])
        .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.Credit)
        .map((fd) => fd.CashValue)
        .reduce((sum, cv) => sum + cv, 0) ?? 0,
  };

  const updatedTotals: PaymentFormTotals = React.useMemo(() => ({
    total: cartData?.GrandTotalRounded ?? 0,
    subtotal: cartData?.SubTotal ?? 0,
    tax: cartData?.Tax ?? 0,
    discount: cartData?.TotalDiscountAndLoyalty ?? 0,
    rounding: cartData?.RoundingAmount ?? 0,
    feesDonations: cartData?.FeesAndDonations ?? 0,
    changeDue: hasPreauth ? 0 : changeAmountDue,
    totalPaid: hasPreauth ? (cartData?.GrandTotalRounded ?? 0) : totalAmountPaid,
    tip: cartData?.TipAmount ?? 0,
    dutchiePayFees: isDutchiePay
      ? ((cartData?.FeesDonations ?? [])
          .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.DutchiePay)
          .map((fd) => fd.CashValue)
          .reduce((sum, cv) => sum + cv, 0) ?? 0)
      : 0,
    nonCashAdjustmentFees:
      (cartData?.FeesByPaymentType ?? [])
        .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.PinDebit)
        .map((fd) => fd.CashValue)
        .reduce((sum, cv) => sum + cv, 0) ?? 0,
    creditNonCashAdjustmentFees:
      (cartData?.FeesByPaymentType ?? [])
        .filter((fd) => fd.FeePaymentMethod === FeePaymentMethod.Credit)
        .map((fd) => fd.CashValue)
        .reduce((sum, cv) => sum + cv, 0) ?? 0,
  }), [cartData, isDutchiePay, hasPreauth, changeAmountDue, totalAmountPaid]);

  const totals = generiPaySpinOnMobileCheckoutEnabled ? updatedTotals : originalTotals;

  // Handlers
  const handlePaymentSuccess = () => {
    setShowCheckoutSuccess(true);
  };

  const handlePaymentFailure = () => {
    setShowCheckoutSuccess(false);
  };

  return {
    totals,
    showCheckoutSuccess,
    handlePaymentSuccess,
    handlePaymentFailure,
  };
};
